import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, Lightbulb, AlertTriangle, FileText, PlayCircle } from "lucide-react";
import { Badge } from "@/components/admin/ui";

function embedUrl(url) {
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

export function Media({ m }) {
  if (m.type === "photo") return <figure><img src={m.url} alt={m.caption || ""} className="rounded-xl max-h-80 object-cover w-full" data-testid="lesson-photo"/>{m.caption && <figcaption className="text-xs text-neutral-500 mt-1">{m.caption}</figcaption>}</figure>;
  if (m.type === "video") return <video src={m.url} controls className="rounded-xl w-full max-h-80 bg-black" data-testid="lesson-video"/>;
  if (m.type === "embed") return <iframe src={embedUrl(m.url)} title={m.caption || "video"} className="rounded-xl w-full aspect-video" allowFullScreen data-testid="lesson-embed"/>;
  if (m.type === "pdf") return <a href={m.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[hsl(var(--primary))] underline" data-testid="lesson-pdf"><FileText className="w-4 h-4"/>{m.caption || "Open PDF"}</a>;
  return null;
}

export function LessonViewer({ id, onClose }) {
  const [l, setL] = useState(null);
  useEffect(() => { api.get(`/training/lessons/${id}`).then((r) => setL(r.data)).catch(() => onClose()); }, [id, onClose]);

  const setStatus = async (status) => {
    try { await api.post(`/training/lessons/${id}/progress`, { status }); setL({ ...l, my_status: status }); toast.success(status === "completed" ? "Lesson completed ✓" : "Marked in progress"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="lesson-viewer">
        {!l ? <p className="p-6 text-neutral-500">Loading…</p> : (
          <>
            <DialogHeader>
              <p className="eyebrow">{l.category}</p>
              <DialogTitle className="font-display text-2xl" data-testid="lesson-viewer-title">{l.title}</DialogTitle>
              {l.description && <p className="text-sm text-neutral-600">{l.description}</p>}
            </DialogHeader>
            <div className="flex items-center gap-2"><Badge data-testid="lesson-viewer-status">{l.my_status}</Badge>
              {l.my_status !== "completed" && <Button size="sm" variant="outline" onClick={() => setStatus("in_progress")} data-testid="lesson-start"><PlayCircle className="w-4 h-4 mr-1"/>Start</Button>}
              {l.my_status !== "completed" && <Button size="sm" onClick={() => setStatus("completed")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="lesson-complete"><CheckCircle2 className="w-4 h-4 mr-1"/>Mark Completed</Button>}
            </div>
            {l.video_url && <Media m={{ type: "embed", url: l.video_url }}/>}
            <ol className="space-y-6 mt-2">
              {(l.steps || []).map((s, i) => (
                <li key={i} className="flex gap-4" data-testid={`lesson-step-${i}`}>
                  <div className="w-8 h-8 rounded-full bg-neutral-900 text-white flex items-center justify-center font-bold text-sm shrink-0">{i + 1}</div>
                  <div className="flex-1 space-y-3">
                    <h3 className="font-semibold">{s.title}</h3>
                    {s.text && <p className="text-sm text-neutral-700 whitespace-pre-line">{s.text}</p>}
                    {(s.media || []).map((m, j) => <Media key={j} m={m}/>)}
                  </div>
                </li>
              ))}
            </ol>
            {l.tips?.length > 0 && <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm"><div className="font-semibold flex items-center gap-2 mb-1 text-amber-800"><Lightbulb className="w-4 h-4"/>Tips</div><ul className="list-disc pl-5 space-y-0.5">{l.tips.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
            {l.warnings?.length > 0 && <div className="rounded-xl bg-red-50 border border-red-100 p-4 text-sm"><div className="font-semibold flex items-center gap-2 mb-1 text-red-800"><AlertTriangle className="w-4 h-4"/>Warnings</div><ul className="list-disc pl-5 space-y-0.5">{l.warnings.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
            {l.pdf_url && <Media m={{ type: "pdf", url: l.pdf_url, caption: "Download SOP (PDF)" }}/>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
